import * as THREE from 'three';
import gsap from 'gsap';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

// Iluminación tipo estudio: hemisférica para relleno suave + luz principal
// y contraluz para que las piezas impresas muestren volumen sin quemarse.
// `bottomLight` añade una luz tenue desde abajo (menos intensa que la
// principal) para insinuar la parte inferior de la pieza sin igualar el
// contraste de la luz superior.
function addStudioLights(scene, { bottomLight = false } = {}) {
    // Relleno hemisférico más contenido que antes (0.9 -> 0.5): rellenar
    // demasiado lava las sombras y aplana el relieve. Basta para que las caras
    // en sombra no queden negras.
    const hemisphere = new THREE.HemisphereLight(0xffffff, 0x30363f, 0.2);

    // Luz principal más intensa y, sobre todo, MÁS RASANTE que antes
    // (elevación ~30° en vez de ~55°). Un ángulo bajo hace que un relieve poco
    // profundo proyecte una sombra larga y legible sobre la base, que es la
    // pista de profundidad que faltaba. Proyecta sombra.
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.6);
    keyLight.position.set(55, 42, 48);
    keyLight.castShadow = true;
    configureShadow(keyLight);

    // Relleno lateral tenue desde el lado opuesto para que la cara en sombra
    // conserve algo de forma sin borrar el contraste.
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-50, 30, -40);

    // Contraluz/rim rasante: recorta el contorno y acentúa los cantos del
    // relieve, reforzando la sensación de volumen.
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.55);
    rimLight.position.set(-35, 22, -55);

    scene.add(hemisphere, keyLight, fillLight, rimLight);

    if (bottomLight) {
        const underLight = new THREE.DirectionalLight(0xffffff, 0.22);
        underLight.position.set(0, -60, 20);
        scene.add(underLight);
    }
}

// Configura la cámara de sombra ortográfica de una luz direccional para que
// cubra la pieza (el visor trabaja a ~1 unidad = 1 cm y encuadra piezas de
// pocas decenas de unidades). `normalBias` evita el "shadow acne" en las caras
// casi paralelas a la luz sin despegar la sombra de contacto.
function configureShadow(light) {
    light.shadow.mapSize.set(2048, 2048);
    const d = 90;
    light.shadow.camera.left = -d;
    light.shadow.camera.right = d;
    light.shadow.camera.top = d;
    light.shadow.camera.bottom = -d;
    light.shadow.camera.near = 1;
    light.shadow.camera.far = 400;
    light.shadow.bias = -0.0004;
    light.shadow.normalBias = 0.6;
}

// Plano invisible a ras de suelo (y = 0) que SOLO recibe la sombra: con
// `ShadowMaterial` no dibuja nada salvo la sombra de contacto, así la pieza
// queda "asentada" incluso con el grid oculto y el fondo transparente (ideal
// para capturas). No proyecta sombra ni tapa el modelo.
function createShadowCatcher(size = 400) {
    const geometry = new THREE.PlaneGeometry(size, size);
    const material = new THREE.ShadowMaterial({ opacity: 0.28 });
    const plane = new THREE.Mesh(geometry, material);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = 0;
    plane.receiveShadow = true;
    plane.renderOrder = -1;
    return plane;
}

// Material mate con un brillo ligero, similar al acabado de PLA impreso.
function applyPrintedMaterial(object) {
    object.traverse((child) => {
        if (child.isMesh && child.material) {
            if (child.geometry?.getAttribute('position')) {
                // Los bordes con mucha calidad generan muchos triángulos muy
                // juntos. Si el GLB llega con vértices duplicados o normales
                // duras, aparecen bandas/moire en la curva aunque el renderer
                // tenga antialias. Fusionamos con tolerancia mínima y
                // recalculamos normales para que el visor lea la curva suave.
                const smoothedGeometry = mergeVertices(child.geometry, 1e-5);
                smoothedGeometry.deleteAttribute('normal');
                smoothedGeometry.computeVertexNormals();
                child.geometry.dispose();
                child.geometry = smoothedGeometry;
            }

            const isDrawingPart = String(child.name || '').startsWith('drawing_');
            const tuneMaterial = (sourceMaterial) => {
                const printedMaterial = sourceMaterial.clone();
                printedMaterial.flatShading = false;
                printedMaterial.roughness = isDrawingPart ? 0.96 : 0.72;
                printedMaterial.metalness = 0.0;
                printedMaterial.envMapIntensity = isDrawingPart ? 0.02 : 0.5;
                printedMaterial.needsUpdate = true;
                return printedMaterial;
            };

            child.material = Array.isArray(child.material)
                ? child.material.map(tuneMaterial)
                : tuneMaterial(child.material);
        }
    });
}

// Centra el modelo en el origen y apoya su base sobre la malla (y = 0).
function placeOnGround(object) {
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());

    object.position.x -= center.x;
    object.position.z -= center.z;
    object.position.y -= box.min.y;
}

// Resuelve un color del tema DaisyUI activo (p. ej. "text-base-content")
// renderizando un elemento oculto y leyendo su color computado.
function getThemeColor(className, fallback) {
    const probe = document.createElement('div');
    probe.className = className;
    probe.style.display = 'none';
    document.body.appendChild(probe);
    const cssColor = getComputedStyle(probe).color;
    document.body.removeChild(probe);

    try {
        return new THREE.Color(cssColor);
    } catch {
        return new THREE.Color(fallback);
    }
}

// Grid discreto basado en los colores del design system: líneas a medio
// camino entre el fondo (base-300) y el texto (base-content) del tema activo.
function createSubtleGrid(size = 100, divisions = 100) {
    const surface = getThemeColor('text-base-300', 0x3d4250);
    const content = getThemeColor('text-base-content', 0x7a8194);

    const majorLines = surface.clone().lerp(content, 0.5);
    const minorLines = surface.clone().lerp(content, 0.22);

    const grid = new THREE.GridHelper(size, divisions, majorLines, minorLines);
    grid.material.opacity = 0.45;
    grid.material.transparent = true;
    return grid;
}

// Mapa de entorno (IBL) suave a partir de un "estudio" virtual. Es lo que hace
// que una base negra mate no se vaya a negro puro: las caras que no miran a
// ninguna luz reciben un degradado ambiental que revela su curvatura. No pinta
// fondo (queda transparente); solo ilumina.
function addEnvironment(scene, renderer) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTexture;
    pmrem.dispose();
    return envTexture;
}

// Encuadra la cámara sobre `object` con el mismo zoom-out y ángulo de
// elevación en los tres visores (Viewer2, Mi3DViewer, Public3dViewer): un
// factor de zoom generoso (3x el ajuste "justo") para que el grid se lea como
// una referencia de tamaño real alrededor de la pieza, y una elevación
// proporcional a la distancia para mantener el mismo ángulo de cámara sea
// cual sea la escala del modelo. Devuelve una promesa que se resuelve cuando
// termina la animación, para poder encadenarla (p. ej. el botón de reset).
function frameCameraOnObject(camera, controls, object, { duration = 2, ease, zoom = 3.0 } = {}) {
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraZ *= zoom;
    const cameraY = cameraZ * (56 / 99);

    return new Promise((resolve) => {
        gsap.to(camera.position, {
            duration,
            ease,
            x: center.x,
            y: cameraY,
            z: cameraZ,
            onUpdate: () => {
                camera.lookAt(center);
                controls.update();
            },
            onComplete: resolve,
        });

        gsap.to(controls.target, {
            duration,
            ease,
            x: center.x,
            y: center.y,
            z: center.z,
            onUpdate: () => controls.update(),
        });
    });
}

export { addStudioLights, addEnvironment, applyPrintedMaterial, placeOnGround, createSubtleGrid, createShadowCatcher, frameCameraOnObject };
